import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartModule } from 'primeng/chart';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';

import { AppStateHolder, TimeLine } from '../../../../shared';
import { UserActivitiesService } from '../user-activities.service';

/** Interface for tracking state statistics for accessibility */
interface StateStatistics {
  opened: number;
  working: number;
  closed: number;
}

@Component({
    selector: 'state-chart',
    templateUrl: 'activity-stateChart.component.html',
    styleUrls: ['activity-stateChart.component.scss'],
    standalone: true,
    imports: [TooltipModule, ChartModule, ProgressSpinnerModule, DatePipe, TranslateModule]
})

export class ActivityStateChartComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private appHolder = inject(AppStateHolder);
  private timeLine = inject(TimeLine);
  private tralsator = inject(TranslateService);
  private service = inject(UserActivitiesService);

  /** Reference to the accessible data table for focus management */
  @ViewChild('stateDataTable') stateDataTable: ElementRef<HTMLTableElement>;

  @Input() modify: Boolean;
  @Input() idx: number;
  @Input() border: boolean;
  @Input() params: string;
  @Input() changedParams: any;
  @Input() widgetPosition: any;

  @Output() dimensionSelected: EventEmitter<any> = new EventEmitter();
  @Output() popup: EventEmitter<{ widx: number, pop: boolean, params: any }> = new EventEmitter();

  requestsSubscribe: Subscription;
  status: string;
  activities: any[];
  dataOfState: any;
  graphOptions: any;
  totalActivities: number = 0;

  /** State statistics for accessibility data table */
  stateStats: StateStatistics = { opened: 0, working: 0, closed: 0 };

  openOptions: boolean = false;
  optionSelected: any;

  finalDate: string;
  initialDate: string;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {

  }

  ngOnInit(): void {
    if (!this.modify) {
      this.timeLine.init();
      this.loadActivities(this.params);
    }
  }

  loadActivities(params: string) {
    this.status = 'busy';

    // Default applicati sempre: periodo settimana, nessuna tipologia.
    // Vengono sovrascritti solo se l'utente ha salvato parametri validi.
    let dateType = 1; // Week
    let typologies = null;

    const prms = this.parseParams(params);
    if (prms) {
      if (prms.date == 'Day')
        dateType = 0;
      else if (prms.date == 'Week')
        dateType = 1;
      else if (prms.date == 'Month')
        dateType = 2;
      else if (prms.date == 'all')
        dateType = 1;

      if (prms.typologies)
        typologies = prms.typologies;
    }

    // Calcola sempre initialDate/finalDate: senza un periodo valido la GET
    // non partirebbe e il widget resterebbe bloccato sullo spinner.
    this.dataClick(dateType);

    this.requestsSubscribe = this.service.getUserActivitiesFil(this.initialDate, this.finalDate, "All", typologies)
      .subscribe({
        next: res => {
          this.status = 'ready';
          this.activities = res ?? [];
          this.stateStatics();
        },
        error: () => {
          // Senza error handler lo status resterebbe 'busy' = spinner infinito.
          this.status = 'ready';
          this.activities = [];
          this.stateStatics();
        }
      });
  }

  /**
   * Deserializza in modo difensivo i parametri salvati del widget.
   * Gestisce il doppio wrapping ({ params: "..." }) e i valori non validi
   * (oggetti gia deserializzati, "null", JSON malformato) ritornando null,
   * cosi da ricadere sui default senza far fallire il caricamento.
   */
  private parseParams(params: any): any {
    if (!params) return null;

    try {
      let prms = typeof params === 'string' ? JSON.parse(params) : params;
      if (prms && prms.params) {
        prms = typeof prms.params === 'string' ? JSON.parse(prms.params) : prms.params;
      }
      return prms ?? null;
    } catch {
      return null;
    }
  }

  hasActivities(): Boolean {

    if (this.activities === undefined)
      return false;

    return (this.activities.length > 0);
  }

  stateStatics() {

    let opened = this.activities.filter((state) => {
      return state.workingState == 0
    })
    let working = this.activities.filter((state) => {
      return state.workingState == 1
    })
    let closed = this.activities.filter((state) => {
      return state.workingState == 2
    })
    let stats = [];

    stats.push(opened.length, working.length, closed.length,);

    // Store state statistics for accessibility data table
    this.stateStats = {
      opened: opened.length,
      working: working.length,
      closed: closed.length
    };
    this.totalActivities = opened.length + working.length + closed.length;

    this.dataOfState = {
      labels: [this.tralsator.instant("chart-view.label-opened"), this.tralsator.instant("chart-view.label-working"), this.tralsator.instant("chart-view.label-closed"),],
      datasets: [
        {
          data: stats,
          backgroundColor: ["#df3a2c", "#e0972b", "#43a047"],
          hoverBackgroundColor: ["#c93023", "#c9831f", "#388e3c"],
          borderWidth: 0,
          spacing: 3,
          borderRadius: 6
        }

      ]
    }
    this.graphOptions = {
      responsive: true,
      maintainAspectRatio: true,
      cutout: '72%',
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(20,24,31,.92)',
          titleFont: { size: 12, weight: '700' },
          bodyFont: { size: 12 },
          padding: 10,
          cornerRadius: 10,
          boxPadding: 4,
          displayColors: true
        }
      },
      animation: { animateRotate: true, animateScale: true, duration: 900, easing: 'easeOutQuart' }
    };
  }

  options() {
    this.openOptions = !this.openOptions;
  }

  selected() {
    this.dimensionSelected.next(this.optionSelected);
  }

  actpop() {

    if (this.changedParams == undefined) {
      this.sendParams();
    }
    else {
      let val = this.appHolder.stateGraphParams.filter(r => { return r.indx == this.idx });

      if (val.length > 0) {
        this.popup.next({ widx: this.idx, pop: true, params: JSON.parse(val[0].params) });
      }
      else {
        this.sendParams();
      }
    }
  }

  sendParams() {

    if (this.params) {
      this.popup.next({ widx: this.idx, pop: true, params: JSON.parse(this.params) });
    }
    else {
      this.popup.next({ widx: this.idx, pop: true, params: this.params });
    }
  }

  dataClick(dateType: number) {

    let day;
    let week;
    let month;

    switch (dateType) {
      case 0:

        day = this.timeLine.day.date;

        this.finalDate = this.initialDate = (day.getFullYear() + "/" + (day.getMonth() + 1) + "/" + day.getDate());
        break;
      case 1:

        week = this.timeLine.week;
        let d1 = week.days[0].date;
        let d2 = week.days[week.days.length - 1].date;

        this.initialDate = d1.getFullYear() + "/" + (d1.getMonth() + 1) + "/" + d1.getDate();
        this.finalDate = d2.getFullYear() + "/" + (d2.getMonth() + 1) + "/" + d2.getDate();

        break;

      case 2:

        this.initialDate = this.timeLine.month.date.getFullYear() + "/" + (this.timeLine.month.date.getMonth() + 1) + "/" + this.timeLine.month.date.getDate();
        this.finalDate = this.timeLine.month.lastDate.getFullYear() + "/" + (this.timeLine.month.lastDate.getMonth() + 1) + "/" + this.timeLine.month.lastDate.getDate();

        break;

      case 3:

        this.initialDate = null;
        this.finalDate = null;

        break;
    }
  }

  ngOnDestroy(): void {
    if (this.requestsSubscribe)
      this.requestsSubscribe.unsubscribe()
  }

  /**
   * Focuses the accessible data table for screen reader users.
   * This method is called when users activate the skip link.
   */
  focusDataTable(): void {
    if (this.stateDataTable?.nativeElement) {
      this.stateDataTable.nativeElement.focus();
    }
  }
}
